import {
  lastGrossMarginAdjustmentSchema,
  lastGrossMarginAdjustmentUpdateSchema,
} from '@snap-types/models/last-gross-margin-adjustment';
import {
  lastTransactionAgeAdjustmentSchema,
  lastTransactionAgeAdjustmentUpdateSchema,
} from '@snap-types/models/last-transaction-age-adjustment';
import { marketIndustryAdjustmentSchema } from '@snap-types/models/market-industry-adjustment';
import { z } from 'zod/v4';

import {
  columns,
  customerPortalsColumns,
  customerPotentialColumns,
  lastGrossMarginColumns,
  lastTransactionAgeColumns,
  marketIndustryColumns,
  productComplexityColumns,
  productOperationsCountColumns,
} from './columns';

export const priceSegmentConfigSchema = z.object(columns);

export const priceSegmentConfigWithRelationsSchema = z.discriminatedUnion(
  'priceSegmentType',
  [
    z.object({ ...columns, ...customerPotentialColumns.shape }),
    z.object({ ...columns, ...customerPortalsColumns.shape }),
    z.object({ ...columns, ...productOperationsCountColumns.shape }),
    z.object({ ...columns, ...productComplexityColumns.shape }),
    z.object({
      ...columns,
      ...marketIndustryColumns.shape,
      marketIndustryAdjustments: z.array(marketIndustryAdjustmentSchema),
    }),
    z.object({
      ...columns,
      ...lastTransactionAgeColumns.shape,
      lastTransactionAgeAdjustments: z.array(
        lastTransactionAgeAdjustmentSchema,
      ),
    }),
    z.object({
      ...columns,
      ...lastGrossMarginColumns.shape,
      lastGrossMarginAdjustments: z.array(lastGrossMarginAdjustmentSchema),
    }),
  ],
);

export type PriceSegmentConfigWithRelations = z.output<
  typeof priceSegmentConfigWithRelationsSchema
>;

const priceSegmentConfigUpdateBaseSchema = priceSegmentConfigSchema.pick({
  sortOrder: true,
  enabled: true,
});

export const priceSegmentConfigUpdateSchema = z.discriminatedUnion(
  'priceSegmentType',
  [
    priceSegmentConfigUpdateBaseSchema.extend(customerPotentialColumns.shape),
    priceSegmentConfigUpdateBaseSchema.extend(customerPortalsColumns.shape),
    priceSegmentConfigUpdateBaseSchema.extend(
      productOperationsCountColumns.shape,
    ),
    priceSegmentConfigUpdateBaseSchema.extend(productComplexityColumns.shape),
    priceSegmentConfigUpdateBaseSchema.extend({
      ...marketIndustryColumns.shape,
      marketIndustryAdjustments: z.array(
        marketIndustryAdjustmentSchema.pick({
          adjustment: true,
          marketCode: true,
        }),
      ),
    }),
    priceSegmentConfigUpdateBaseSchema.extend({
      ...lastTransactionAgeColumns.shape,
      lastTransactionAgeAdjustments: z.array(
        lastTransactionAgeAdjustmentUpdateSchema.pick({
          ageInDays: true,
          adjustment: true,
        }),
      ),
    }),
    priceSegmentConfigUpdateBaseSchema.extend({
      ...lastGrossMarginColumns.shape,
      lastGrossMarginAdjustments: z.array(
        lastGrossMarginAdjustmentUpdateSchema.pick({
          margin: true,
          adjustment: true,
        }),
      ),
    }),
  ],
);

export type PriceSegmentConfig = z.output<typeof priceSegmentConfigSchema>;

export type PriceSegmentConfigUpdate = z.output<
  typeof priceSegmentConfigUpdateSchema
>;
